import React from 'react'

import { Chip, IconButton, Tooltip, useTheme } from '@material-ui/core'
import CategoryIcon from '@material-ui/icons/Category'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import ImageIcon from '@material-ui/icons/Image'
import DescriptionIcon from '@material-ui/icons/Description'
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'

import { Container } from '../../../components'
import {
    formatConditionLabel,
    formatStatusLabel,
    getConditionColor,
    getPrimaryPhoto,
    getStatusColor,
    InventoryAsset,
} from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    asset: InventoryAsset
    onEdit: (asset: InventoryAsset) => void
    onDelete: (asset: InventoryAsset) => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 14,
            padding: 0,
            backgroundColor: theme.palette.background.paper,
            minWidth: 280,
            flex: '1 1 300px',
            maxWidth: 360,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            overflow: 'hidden',
            flexDirection: 'column',
            '&:hover': {
                borderColor: theme.palette.primary.light,
                boxShadow: `0 8px 24px rgba(0,0,0,0.08), 0 0 0 1px ${theme.palette.primary.main}18`,
                transform: 'translateY(-2px)',
            },
            '&:hover $actions': {
                opacity: 1,
            },
        },
        imageSection: {
            width: '100%',
            height: 140,
            position: 'relative',
            backgroundColor: theme.palette.grey[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
        },
        image: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        photoCount: {
            position: 'absolute',
            bottom: 8,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: '#fff',
            borderRadius: 6,
            padding: '3px 8px',
            fontSize: 11,
            fontWeight: 600,
        },
        content: {
            padding: theme.spacing(1.5, 2, 2),
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
        },
        topRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        },
        actions: {
            display: 'flex',
            gap: 2,
            opacity: 0,
            transition: 'opacity 0.2s ease',
        },
    }),
)

export const InventoryAssetCard: React.FC<Props> = ({
    asset,
    onEdit,
    onDelete,
}) => {
    const theme = useTheme()
    const classes = useStyles()
    const photoUrl = getPrimaryPhoto(asset)
    const photoCount = asset.photos?.length ?? 0

    return (
        <Container className={classes.card} onClick={() => onEdit(asset)}>
            {/* Image area */}
            <div className={classes.imageSection}>
                {photoUrl ? (
                    <img
                        src={photoUrl}
                        alt={asset.name}
                        className={classes.image}
                    />
                ) : (
                    <ImageIcon
                        style={{
                            fontSize: 48,
                            color: theme.palette.grey[300],
                        }}
                    />
                )}
                {photoCount > 1 && (
                    <div className={classes.photoCount}>
                        <PhotoLibraryIcon style={{ fontSize: 12 }} />
                        {photoCount}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className={classes.content}>
                <div className={classes.topRow}>
                    <Container
                        style={{
                            flexDirection: 'column',
                            gap: 2,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 700,
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {asset.name}
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {asset.category}
                            {asset.quantity > 1
                                ? ` · Qty ${asset.quantity}`
                                : ''}
                        </span>
                    </Container>
                    <div
                        className={classes.actions}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => onEdit(asset)}
                                style={{ padding: 4 }}
                            >
                                <EditIcon style={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={() => onDelete(asset)}
                                style={{ padding: 4 }}
                            >
                                <DeleteIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.error.light,
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                {(asset.brand || asset.model_number) && (
                    <span
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            color: theme.palette.text.disabled,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {[asset.brand, asset.model_number]
                            .filter(Boolean)
                            .join(' · ')}
                    </span>
                )}

                {asset.inventory_config_name && (
                    <Container
                        style={{
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <CategoryIcon
                            style={{
                                fontSize: 13,
                                color: theme.palette.text.secondary,
                            }}
                        />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {asset.inventory_config_name}
                        </span>
                    </Container>
                )}

                <Container
                    style={{
                        gap: 5,
                        marginTop: 4,
                        flexWrap: 'wrap',
                    }}
                >
                    <Chip
                        size="small"
                        label={formatConditionLabel(asset.condition)}
                        style={{
                            backgroundColor: `${getConditionColor(
                                asset.condition,
                            )}14`,
                            color: getConditionColor(asset.condition),
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            border: `1px solid ${getConditionColor(
                                asset.condition,
                            )}30`,
                        }}
                    />
                    <Chip
                        size="small"
                        label={formatStatusLabel(asset.status)}
                        style={{
                            backgroundColor: `${getStatusColor(
                                asset.status,
                            )}14`,
                            color: getStatusColor(asset.status),
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            border: `1px solid ${getStatusColor(
                                asset.status,
                            )}30`,
                        }}
                    />
                    {(asset.product_profile?.documents?.length ?? 0) > 0 && (
                        <Chip
                            size="small"
                            icon={
                                <DescriptionIcon
                                    style={{
                                        color: theme.palette.info.main,
                                        fontSize: 13,
                                    }}
                                />
                            }
                            label={`${
                                asset.product_profile!.documents.length
                            } doc${
                                asset.product_profile!.documents.length === 1
                                    ? ''
                                    : 's'
                            }`}
                            style={{
                                backgroundColor: `${theme.palette.info.main}10`,
                                color: theme.palette.info.main,
                                fontWeight: 600,
                                fontSize: 11,
                                height: 22,
                                border: `1px solid ${theme.palette.info.main}25`,
                            }}
                        />
                    )}
                </Container>
            </div>
        </Container>
    )
}
